/**
 * Acciones sobre una factura en la pantalla Financiero.
 *
 * "Pagar" abre la página de pago de la propia factura de Stripe (`hostedInvoiceUrl`), no un cobro suelto
 * por el mismo importe: así el pago llega al servidor como `invoice.paid` y la factura y la suscripción
 * se concilian solas. Un cobro suelto no está asociado a la factura y la plataforma no se enteraba.
 */

export type InvoiceLinkKind = "view" | "pdf" | "pay";

export interface InvoiceLinkSet {
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
}

export interface PayableInvoice {
  status: string;
  /** Hay factura en el proveedor de pagos. */
  hasProviderInvoice: boolean;
}

/** Solo se puede pagar en línea una factura pendiente que exista también en el proveedor de pagos. */
export function canPayInvoice(invoice: PayableInvoice): boolean {
  return invoice.hasProviderInvoice && (invoice.status === "ISSUED" || invoice.status === "OVERDUE");
}

/**
 * Dirección que abre cada botón. "Ver" y "PDF" se apoyan una en la otra si falta una; "Pagar" solo vale
 * la página de la factura (un PDF no permite pagar), y sin ella devuelve `null`.
 */
export function pickInvoiceUrl(links: InvoiceLinkSet, kind: InvoiceLinkKind): string | null {
  if (kind === "pay") return links.hostedInvoiceUrl ?? null;
  if (kind === "pdf") return links.pdfUrl ?? links.hostedInvoiceUrl ?? null;
  return links.hostedInvoiceUrl ?? links.pdfUrl ?? null;
}

/** Mensaje cuando no hay dirección que abrir. */
export function missingInvoiceUrlMessage(kind: InvoiceLinkKind): string {
  return kind === "pay"
    ? "Esta factura no se puede pagar en línea ahora mismo. Contacta con soporte."
    : "Esta factura todavía no tiene documento disponible.";
}
